import { useState, useEffect, useCallback, useMemo } from "react";
import apiDeudas from "@/api/Deudas";
import apiPersonas from "@/api/Personas.js";
import { useAuth } from "@/contexts/AuthContext";
import { convertToNumber, toKeyValue } from "@/utils/convert";

const OMIT_RUBROS = [];

export const useDeudas = () => {
  const { user } = useAuth();
  const [deudas, setDeudas] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastQuery, setLastQuery] = useState(null);
  const [statusInfo, setStatusInfo] = useState(null);

  const detectStatus = (data) => {
    if (!data?.Detalle || !Array.isArray(data.Detalle)) return null;
    const first = data.Detalle[0];
    if (!first) return null;
    const mainKey = Object.keys(first).find((k) => k !== "Detalle");
    const detalle = first.Detalle;
    const isFlatDetalle =
      detalle && typeof detalle === "object" && !Array.isArray(detalle);

    if (isFlatDetalle && mainKey) {
      const innerKey = Object.keys(detalle)[0];
      return {
        type: "INFO",
        title: mainKey,
        count: first[mainKey],
        message: detalle[innerKey],
        code: innerKey,
      };
    }

    return null;
  };

  const fetchDeudas = useCallback(
    async (data) => {
      try {
        const cedula = data?.identification ?? user?.cedularuc;
        // const cedula = "1307534709";
        if (!cedula) return;

        setIsLoading(true);
        setError(null);
        setDeudas({});
        setStatusInfo(null);

        if (!cedula) {
          setError("Información proporcionada incorrecta");
          setDeudas({});
          return;
        }

        if (!user?.cedularuc) {
          let resPer = await apiPersonas.getDatos(cedula);
          if (!toKeyValue(resPer.data.data)?.cedularuc) {
            return;
          }
        }

        const res = await apiDeudas.get(cedula, data?.captchaToken);
        const parsed = toKeyValue(res.data.data);

        parsed.Detalle =
          parsed?.Detalle?.filter((item) => {
            const key = Object.keys(item)
              .find((k) => k !== "Detalle")
              .toLowerCase();
            return !OMIT_RUBROS.includes(key?.toLowerCase());
          }) || [];
        const status = detectStatus(parsed);

        if (status) {
          setStatusInfo(status);
          parsed.Detalle =
            parsed?.Detalle?.filter(
              (item) => !Object.keys(item).includes("Declaraciones Pendientes"),
            ) || [];
        } else {
          setStatusInfo(null);
        }

        setLastQuery(cedula);
        setDeudas(parsed);
        return parsed;
      } catch {
        setError(
          "Ha ocurrido un error, si persiste envie un correo a dinformatica@portoviejo.gob.ec",
        );
        setDeudas({});
      } finally {
        setIsLoading(false);
      }
    },
    [user?.cedularuc],
  );

  const total = useMemo(() => {
    if (!deudas?.Total) return 0;
    if (OMIT_RUBROS.length === 0) {
      const n = convertToNumber(deudas.Total);
      return Math.round(n * 100) / 100;
    } else {
      const totalFiltered =
        deudas?.Detalle?.reduce((acc, item) => {
          const key = Object.keys(item).find((k) => k !== "Detalle");
          return acc + convertToNumber(item[key]);
        }, 0) || 0;
      return Math.round(totalFiltered * 100) / 100;
    }
  }, [deudas]);

  const rubros = useMemo(() => {
    if (!deudas?.Detalle) return [];

    return deudas.Detalle.map((rubro) => {
      const [rubroKey, rubroValue] = Object.entries(rubro).find(
        ([key]) => key !== "Detalle",
      );

      let subDetalle = [];

      if (Array.isArray(rubro.Detalle)) {
        subDetalle = rubro.Detalle.map((item) => {
          const [tituloKey, tituloValue] = Object.entries(item).find(
            ([key]) => key !== "Detalle",
          );

          return {
            titulo: tituloKey,
            valor: tituloValue,
            detalle: item.Detalle,
          };
        });
      }

      return {
        rubro: rubroKey,
        valor: rubroValue,
        detalle: subDetalle,
      };
    });
  }, [deudas]);

  const clearData = useCallback(() => {
    setDeudas({});
    setIsLoading(false);
    setError(null);
    setLastQuery(null);
  }, []);

  useEffect(() => {
    fetchDeudas();
  }, [fetchDeudas]);

  return {
    deudas,
    total,
    isLoading,
    error,
    refetch: fetchDeudas,
    rubros,
    lastQuery,
    clearData,
    statusInfo,
  };
};
